import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import {
  Button,
  IconButton,
  Chip as MuiChip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Close, Upload } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

const Chip = styled(MuiChip)<{ color?: string }>`
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"].light};
  color: ${(props) => props.theme.palette.common.white};
  margin-bottom: ${(props) => props.theme.spacing(4)};
`;

interface ExcelImportPreviewDialogProps {
  onImport: (selectedData: any[]) => Promise<void>;
  existingItems?: (string | undefined)[]; // the existing items must at least have a title so we can check for duplicates
}

const ExcelImportPreviewDialog: React.FC<ExcelImportPreviewDialogProps> = ({
  onImport,
  existingItems,
}) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [duplicates, setDuplicates] = useState<number[]>([]);

  const [columnMapping, setColumnMapping] = useState<string[]>([]); // the index is the column index in the excel file, the value is the column name in the database
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);

  const [open, setOpen] = useState(false);
  const [fileData, setFileData] = useState<any[]>([]);

  useEffect(() => {
    // Reset mapping if columns change
    // setColumnMapping(defaultMapping);

    const newExcelColumns =
      fileData && fileData.length > 0 ? Object.keys(fileData[0]) : [];
    setExcelColumns(newExcelColumns);

    // Set default mapping
    const defaultMapping = newExcelColumns.map((col) => col);
    setColumnMapping(defaultMapping);
  }, [fileData]);

  useEffect(() => {
    if (
      columnMapping?.length > 0 &&
      existingItems &&
      existingItems?.length > 0
    ) {
      // Check for duplicates by comparing titles
      const duplicateIndices = fileData
        .map((row, index) => {
          const isDuplicate = existingItems.some(
            (existingItem) => existingItem === row[columnMapping[0]]
          );
          return isDuplicate ? index : -1;
        })
        .filter((index) => index !== -1);
      setDuplicates(duplicateIndices);
    }
  }, [fileData, existingItems, columnMapping]);

  const handleFileUpload = async (event: any) => {
    const f = event.target.files[0];
    if (!f) {
      return;
    }

    const reader = new FileReader();

    let jsonData: any[] = [];

    reader.onerror = (error) => {
      console.error("Error reading file:", error);

      setFileData([]);
      setSelectedRows([]);
      setDuplicates([]);
      setOpen(false);
    };

    reader.onload = (e: any) => {
      setOpen(true);

      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      jsonData = XLSX.utils.sheet_to_json(sheet);
      console.log(jsonData); // show preview

      setFileData(jsonData);
    };

    reader.readAsArrayBuffer(f);

    // if (file) {
    //   if (file.mimetype === "text/csv" || file.originalname.endsWith(".csv")) {
    //     // Parse CSV
    //     const csvString = file.buffer.toString("utf-8");
    //     jsonData = csvParse.parse(csvString, {
    //       columns: true,
    //       skip_empty_lines: true,
    //     });
    //   } else {
    //     // Parse Excel
    //     const workbook = xlsx.read(file.buffer, { type: "buffer" });
    //     const sheetName = workbook.SheetNames[0];
    //     const sheet = workbook.Sheets[sheetName];
    //     jsonData = xlsx.utils.sheet_to_json(sheet);
    //   }

    //   // If this is a preview request, just return the parsed data
    //   if (req.body.preview === "true") {
    //     return res.status(200).send({
    //       message: "Successfully parsed file",
    //       data: jsonData,
    //     });
    //   }
    // }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(fileData.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (index: number) => {
    setSelectedRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleImport = async () => {
    // Pass mapping info along with selected data
    const selectedData = fileData
      .filter((_, index) => selectedRows.includes(index))
      .map((row) => ({
        title: row[columnMapping[0]],
        body: row[columnMapping[1]],
        _original: row,
      }));
    try {
      await onImport(selectedData);
    } catch (error) {
      console.error("Error importing data:", error);
    }
    setImporting(false);
  };

  const onClose = () => {
    setFileData([]);
    setSelectedRows([]);
    setDuplicates([]);
    setOpen(false);
  };

  // Dropdown change handler
  const handleMappingChange = (columnIndex: number, value: string) => {
    const newColumnMapping = [...columnMapping];

    newColumnMapping[columnIndex] = value;
    setColumnMapping(newColumnMapping);
  };

  return (
    <Grid sx={{ display: "flex", alignItems: "center" }}>
      <input
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        id="excel-upload"
        onChange={handleFileUpload}
        disabled={importing}
      />
      <label htmlFor="excel-upload">
        <Button component="span" startIcon={<Upload />} disabled={importing}>
          {importing ? t("Importing...") : t("Import")}
        </Button>
      </label>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>{t("Import Preview")}</Grid>
            <Grid>
              <IconButton size="small" onClick={onClose}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          {duplicates.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("DuplicateRowsFound", {
                count: duplicates.length,
              })}
            </Alert>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.length === fileData.length}
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length < fileData.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    {columnMapping.length > 0 && (
                      <Autocomplete
                        value={columnMapping[0]}
                        onChange={(e, option) => {
                          if (option) handleMappingChange(0, option);
                          else handleMappingChange(0, "");
                        }}
                        fullWidth
                        options={excelColumns}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label={t("Title")}
                            variant="outlined"
                          />
                        )}
                        disableClearable
                        defaultValue={columnMapping[0]}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {columnMapping.length > 1 && (
                      <Autocomplete
                        value={columnMapping[1]}
                        onChange={(e, option) => {
                          if (option) handleMappingChange(1, option);
                          else handleMappingChange(1, "");
                        }}
                        fullWidth
                        options={excelColumns}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label={t("Body")}
                            variant="outlined"
                          />
                        )}
                        disableClearable
                        defaultValue={columnMapping[1]}
                      />
                    )}
                  </TableCell>
                  <TableCell>{t("Status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileData.map((row, index) => (
                  <TableRow
                    key={index}
                    selected={selectedRows.includes(index)}
                    sx={{
                      backgroundColor: duplicates.includes(index)
                        ? "rgba(255, 152, 0, 0.1)"
                        : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(index)}
                        onChange={() => handleSelectRow(index)}
                      />
                    </TableCell>
                    <TableCell>{row[columnMapping[0]] || ""}</TableCell>
                    <TableCell>
                      {(columnMapping[1] && row[columnMapping[1]]) || ""}
                    </TableCell>
                    <TableCell>
                      {duplicates.includes(index) ? (
                        <Chip
                          label={t("Duplicate")}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Chip label={t("New")} color="success" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={selectedRows.length === 0 || importing}
          >
            {t("ImportRow", {
              count: selectedRows.length,
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ExcelImportPreviewDialog;
